export type AlarmItemLevel = 'info' | 'caution' | 'warning' | 'measure';

export interface AlarmEventDisplayMeta {
  label: string;
  color: string;
  level: AlarmItemLevel;
}

const SACON_PRODUCT_PROCESSED_EVENT = 'SACON_PRODUCT_PROCESSED';
const SACON_PRODUCT_INFO_EVENT = 'SACON_PRODUCT_INFO';
const SACON_PRODUCT_WARNING_EVENT = 'SACON_PRODUCT_WARNING';
const SACON_PRODUCT_ALERT_EVENT = 'SACON_PRODUCT_ALERT';
const SACON_PRODUCT_ERROR_EVENT = 'SACON_PRODUCT_ERROR';
const WARN_MESSAGE_TYPE = 'WARN';
const ALERT_MESSAGE_TYPE = 'ALERT';

const ALARM_EVENT_DISPLAY_MAP: Record<string, AlarmEventDisplayMeta> = {
  [SACON_PRODUCT_PROCESSED_EVENT]: { label: '계측', color: '#10B981', level: 'measure' },
  [SACON_PRODUCT_INFO_EVENT]: { label: '정보', color: '#3B82F6', level: 'info' },
  [SACON_PRODUCT_WARNING_EVENT]: { label: '주의', color: '#F59E0B', level: 'caution' },
  [SACON_PRODUCT_ALERT_EVENT]: { label: '경고', color: '#EF4444', level: 'warning' },
  [SACON_PRODUCT_ERROR_EVENT]: { label: '오류', color: '#6B7280', level: 'info' },
  ISSUED_ASSET_RETURN_DUE: { label: '정보', color: '#3B82F6', level: 'info' },
  ISSUED_ASSET_RETURN_OVERDUE: { label: '주의', color: '#F59E0B', level: 'caution' },
  SAFETY_GUARD_MISSING_WORKER_ALERT: { label: '경고', color: '#EF4444', level: 'warning' },
  CONSTRUCTION_EQUIPMENT_RENTAL_MISMATCH_ALERT: { label: '경고', color: '#EF4444', level: 'warning' },
  MEDIA_GATEWAY_DEGRADED: { label: '주의', color: '#F59E0B', level: 'caution' },
  DEVICE_GEOFENCE_ESCAPE: { label: '경고', color: '#EF4444', level: 'warning' },
};

/** 이벤트 타입을 화면 표시용 라벨·색상·레벨로 변환합니다. */
export function resolveAlarmEventDisplay(event: unknown): AlarmEventDisplayMeta | null {
  if (typeof event !== 'string') return null;
  return ALARM_EVENT_DISPLAY_MAP[event.trim().toUpperCase()] ?? null;
}

/** 이벤트 타입을 알림 목록용 레벨로 변환합니다. */
export function mapAlarmEventToAlarmItemLevel(event: unknown): AlarmItemLevel {
  return resolveAlarmEventDisplay(event)?.level ?? 'caution';
}

/** 메시지 타입과 이벤트를 함께 반영해 알림 목록 표시 등급을 결정합니다. */
export function mapAlarmMessageToAlarmItemLevel(event: unknown, messageType: unknown): AlarmItemLevel {
  const normalizedEvent = typeof event === 'string' ? event.trim().toUpperCase() : '';
  const normalizedMessageType = typeof messageType === 'string' ? messageType.trim().toUpperCase() : '';
  const eventDisplay = resolveAlarmEventDisplay(normalizedEvent);

  if (normalizedEvent === SACON_PRODUCT_ERROR_EVENT) {
    return eventDisplay?.level ?? 'info';
  }
  if (normalizedMessageType === WARN_MESSAGE_TYPE) {
    return 'caution';
  }
  if (normalizedMessageType === ALERT_MESSAGE_TYPE) {
    return 'warning';
  }
  return eventDisplay?.level ?? 'caution';
}

/** 지도 자동 포커스에서 제외할 진단·정보·계측 이벤트인지 확인합니다. */
export function isInfoOrMeasureAlarmEvent(event: unknown): boolean {
  const normalizedEvent = typeof event === 'string' ? event.trim().toUpperCase() : '';
  return normalizedEvent === SACON_PRODUCT_INFO_EVENT || normalizedEvent === SACON_PRODUCT_PROCESSED_EVENT || normalizedEvent === SACON_PRODUCT_ERROR_EVENT;
}
